import React from 'react';
import styled from 'styled-components';
import { Icon } from '../icon';
import PdfIcon from 'src/shared/file-type-icons/tabler/file-type-pdf.svg';
import CsvIcon from 'src/shared/file-type-icons/tabler/file-type-csv.svg';
import SpreadsheetIcon from 'src/shared/file-type-icons/tabler/file-type-xls.svg';
import DocumentIcon from 'src/shared/file-type-icons/tabler/file-type-doc.svg';
import PresentationIcon from 'src/shared/file-type-icons/tabler/file-type-ppt.svg';
import ArchiveIcon from 'src/shared/file-type-icons/tabler/file-type-zip.svg';
import TextIcon from 'src/shared/file-type-icons/tabler/file-type-txt.svg';
import CodeIcon from 'src/shared/file-type-icons/tabler/file-code.svg';
import AudioIcon from 'src/shared/file-type-icons/tabler/file-music.svg';
import ModelIcon from 'src/shared/file-type-icons/tabler/file-3d.svg';
import ImageIcon from 'src/shared/file-type-icons/tabler/photo.svg';
import GenericIcon from 'src/shared/file-type-icons/tabler/file-description.svg';

const types = {
    pdf: { icon: PdfIcon, color: '#d74646' },
    csv: { icon: CsvIcon, color: '#21865b' },
    spreadsheet: { icon: SpreadsheetIcon, color: '#21865b' },
    document: { icon: DocumentIcon, color: '#3674c8' },
    presentation: { icon: PresentationIcon, color: '#ca642f' },
    archive: { icon: ArchiveIcon, color: '#927128' },
    text: { icon: TextIcon, color: '#65738b' },
    code: { icon: CodeIcon, color: '#7b56b8' },
    audio: { icon: AudioIcon, color: '#ae4d8a' },
    model: { icon: ModelIcon, color: '#5770b5' },
    image: { icon: ImageIcon, color: '#5770b5' },
    file: { icon: GenericIcon, color: '#7c8399' }
};
type FileType = keyof typeof types;

const extensions: Record<string, FileType> = {
    pdf: 'pdf',
    csv: 'csv',
    tsv: 'csv',
    xls: 'spreadsheet',
    xlsx: 'spreadsheet',
    xlsm: 'spreadsheet',
    ods: 'spreadsheet',
    doc: 'document',
    docx: 'document',
    odt: 'document',
    rtf: 'document',
    ppt: 'presentation',
    pptx: 'presentation',
    odp: 'presentation',
    zip: 'archive',
    rar: 'archive',
    '7z': 'archive',
    tar: 'archive',
    gz: 'archive',
    txt: 'text',
    md: 'text',
    json: 'code',
    xml: 'code',
    html: 'code',
    htm: 'code',
    yaml: 'code',
    yml: 'code',
    jpg: 'image',
    jpeg: 'image',
    png: 'image',
    gif: 'image',
    webp: 'image',
    svg: 'image',
    avif: 'image',
    tif: 'image',
    tiff: 'image',
    bmp: 'image',
    mp3: 'audio',
    wav: 'audio',
    ogg: 'audio',
    flac: 'audio',
    m4a: 'audio',
    glb: 'model',
    gltf: 'model',
    obj: 'model',
    fbx: 'model',
    stl: 'model'
};

export function getFileType(name: string, contentType = ''): FileType {
    const extension = name.split('.').pop()?.toLowerCase() ?? '';
    if (name.includes('.') && Object.hasOwn(extensions, extension)) return extensions[extension];
    const mime = contentType.split(';')[0].trim().toLowerCase();
    if (mime === 'application/pdf') return 'pdf';
    if (mime === 'text/csv' || mime === 'text/tab-separated-values') return 'csv';
    if (mime.includes('spreadsheet') || mime === 'application/vnd.ms-excel') return 'spreadsheet';
    if (mime.includes('wordprocessing') || mime.includes('opendocument.text') || mime === 'application/msword') return 'document';
    if (mime.includes('presentation') || mime === 'application/vnd.ms-powerpoint') return 'presentation';
    if (mime.includes('zip') || mime.includes('compressed') || mime === 'application/x-tar') return 'archive';
    if (mime.startsWith('image/')) return 'image';
    if (mime.startsWith('audio/')) return 'audio';
    if (mime.startsWith('model/')) return 'model';
    if (['application/json', 'application/xml', 'text/xml', 'text/html'].includes(mime)) return 'code';
    if (mime.startsWith('text/')) return 'text';
    return 'file';
}

const FileGlyph = styled(Icon)<{ $color: string; $size: number }>`
    flex-shrink: 0;
    width: ${(p) => p.$size}px;
    height: ${(p) => p.$size}px;
    color: ${(p) => p.$color};
    stroke-width: 1.5;
    svg {
        width: 100%;
        height: 100%;
    }
`;

export function FileTypeIcon({ name, contentType, size = 40 }: { name: string; contentType?: string; size?: number }) {
    const { icon: Glyph, color } = types[getFileType(name, contentType)];
    return (
        <FileGlyph $color={color} $size={size} aria-hidden='true'>
            <Glyph viewBox='0 0 24 24' />
        </FileGlyph>
    );
}
